import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import * as IAction from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { UserService } from 'app/services/user.service';
import { ZkuService } from 'app/services/zku.service';
import { Goals } from 'modules/experiments/utils/Goals';
import {
	PromptuaryBreadcrumb,
	PromptuaryErrors,
	SentryPromptuaryErrorsMessages,
} from 'modules/promptuary/interfaces/promptuary.interface';
import { IPromptuary } from 'shared/interfaces/promptuary.interface';
import { AppointmentService } from 'shared/services/appointment/appointment.service';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

import { PromptuarySidebarService } from './../../services/sidebar/promptuary-sidebar.service';
import { UnifiedPromptuaryService } from '../promptuary-form/unified-promptuary.service';

@Component({
	selector: 'promptuary-page',
	templateUrl: './promptuary-page.component.html',
	styleUrls: ['./promptuary-page.component.scss'],
	providers: [PromptuaryService],
})
export class PromptuaryPage implements OnInit, OnDestroy {
	loading = true;
	promptuary: IPromptuary;
	appointmentId: string;
	clientId: string;
	psychiatrist: boolean;
	psychotherapist: boolean;
	psychologist: boolean;
	isAssistanceJourney: boolean;
	firstName: string;
	goals = Goals;
	mainType;
	from = 'direct';
	constructor(
		public actionTrackerService: ActionTrackerService,
		public activatedRoute: ActivatedRoute,
		public location: Location,
		public notificationService: ZkNotificationService,
		public promptuaryService: PromptuaryService,
		public appointmentService: AppointmentService,
		public translateService: TranslateService,
		public userService: UserService,
		public zkuService: ZkuService,
		public router: Router,
		public unifiedPromptuary: UnifiedPromptuaryService,
		public promptuarySidebarService: PromptuarySidebarService
	) {
		this.from = this.router.getCurrentNavigation()?.extras?.state?.from;
		this.from = this.from ? this.from : 'direct';

		this.activatedRoute.params.subscribe((params) => {
			this.appointmentId = params['appointmentId'];
			this.clientId = params['clientId'];
		});
	}

	ngOnInit(): void {
		this.unifiedPromptuary.setBreadcrumb(PromptuaryBreadcrumb.PAGE_OPEN);
		this.getProfileData();
	}

	ngOnDestroy(): void {
		this.unifiedPromptuary.setBreadcrumb(PromptuaryBreadcrumb.PAGE_CLOSE);
		this.unifiedPromptuary.resetPromptuary();
	}

	getProfileData() {
		this.userService
			.getProfileData()
			.then(({ mainType }) => {
				this.mainType = mainType;
				this.psychiatrist = mainType === 'psychiatrist';
				this.psychologist = mainType === 'psychologist';
				this.getAppointment();
			})
			.catch(() => {
				this.unifiedPromptuary.capturePromptuaryError(
					PromptuaryErrors.GET_PROFILE_ERROR,
					SentryPromptuaryErrorsMessages.GET_PROFILE_ERROR
				);
				this.goBack();
			});
	}

	getAppointment() {
		this.appointmentService
			.getData(this.appointmentId)
			.toPromise()
			.then((appointment) => {
				this.isAssistanceJourney = Boolean(
					appointment?.subscription?.coordinatedCareType
				);
				this.psychotherapist = !this.psychiatrist && this.isAssistanceJourney;
				this.firstName = appointment.userName.split(' ')[0];
				this.getPromptuary(this.appointmentId, this.clientId);
			})
			.catch(() => {
				this.unifiedPromptuary.capturePromptuaryError(
					PromptuaryErrors.GET_APPOINTMENT_ERROR,
					SentryPromptuaryErrorsMessages.GET_APPOINTMENT_ERROR
				);
				this.goBack();
			});
	}

	getPromptuary(appointmentId: string, clientId: string): void {
		this.unifiedPromptuary.resetPromptuary();
		const professionalId = this.zkuService.getZku().id;
		this.promptuaryService.getData(appointmentId, clientId).subscribe(
			(result) => {
				this.promptuary = result;
				this.unifiedPromptuary.promptuaryMode = this.promptuary.isRegistered
					? 'view'
					: 'manage';

				if (
					this.unifiedPromptuary.promptuaryMode === 'manage' &&
					!this.psychiatrist
				) {
					this.openSidebar();
				}
				this.unifiedPromptuary.isLoading = false;
				this.unifiedPromptuary.isPromptuaryCreated = true;
				this.trackPromptuarySeen(appointmentId, clientId, professionalId);
			},
			({ error }) => {
				this.promptuary = null;
				if (error?.statusCode === 404) {
					this.unifiedPromptuary.setBreadcrumb(
						PromptuaryBreadcrumb.NOT_FOUND_ERROR
					);
					this.unifiedPromptuary.createPromptuary(clientId, appointmentId);
					return;
				}
				this.unifiedPromptuary.capturePromptuaryError(
					PromptuaryErrors.GET_PROMPTUARY_GENERIC_ERROR,
					SentryPromptuaryErrorsMessages.GET_PROMPTUARY_GENERIC_ERROR
				);
				this.goBack();
			}
		);
	}

	trackPromptuarySeen(appointmentId, clientId, professionalId) {
		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Promptuary,
			IAction.SUBSCOPE_TYPE.Session,
			IAction.ITEM_TYPE.Status,
			IAction.INTERACTION_TYPE.Seen,
			{
				sessionId: appointmentId,
				contactId: clientId,
				professionalId: professionalId,
				registered: this.promptuary.isRegistered,
			}
		);
	}

	updateState() {
		this.getPromptuary(this.appointmentId, this.clientId);
	}

	stopLoader() {
		this.loading = false;
	}

	openSidebar() {
		this.router.navigate(['/calendar']);
		this.promptuarySidebarService.openSidebarOrGoToPage(
			this.clientId,
			this.appointmentId,
			'promptuary-view'
		);
	}

	goBack() {
		this.stopLoader();
		this.location.back();
	}
}
